import type { ReactNode } from 'react';
import { ToggleButtonGroup } from '@heroui/react/toggle-button-group';
import { ToggleButton } from '@heroui/react/toggle-button';
import { cn } from '@/utils/cn';

/**
 * Two-to-four mutually exclusive views (Active / Archived, Adult / Pedo).
 *
 * Built on HeroUI's ToggleButtonGroup (react-aria underneath), so roving
 * focus, arrow-key navigation and the radio-group semantics are handled for
 * us. It previously open-coded `<button role="tab">` with hand-painted
 * selected states, which announced itself as a tablist without any tabpanel
 * to point at.
 */
export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  isDisabled?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
  fullWidth = false,
  className,
  'aria-label': ariaLabel,
  'data-tour': tourAnchor,
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md' | 'lg';
  /** Stretch across the row, segments sharing the width equally. */
  fullWidth?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Anchor for a guided tour step (see `components/tour`). */
  'data-tour'?: string;
}) {
  return (
    <ToggleButtonGroup
      data-tour={tourAnchor}
      aria-label={ariaLabel}
      size={size}
      fullWidth={fullWidth}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        // react-aria hands back a Set; single-selection means at most one key.
        const next = Array.from(keys)[0];
        if (next != null) onChange(next as T);
      }}
      className={cn('shrink-0', className)}
    >
      {options.map((opt) => (
        <ToggleButton
          key={opt.value}
          id={opt.value}
          isDisabled={opt.isDisabled}
          className="gap-1.5 whitespace-nowrap"
        >
          {opt.icon}
          {opt.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
